import { useContext } from "react"
import NextImage from "next/image"

import { Button } from "@coval/ui"
import {
  mergeAttributes,
  Node,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from "@tiptap/react"
import { GripVertical } from "lucide-react"

import type { NotebookPlotSpec } from "../utils/notebookCanvas"
import { notebookMediaUrl } from "../utils/notebookMedia"
import { NotebookCanvasContext } from "./NotebookCanvasContext"
import { NotebookPlot } from "./NotebookPlot"

function FigureNode({
  node,
  editor,
  selected,
  updateAttributes,
}: NodeViewProps) {
  const { assets, renderEvidence } = useContext(NotebookCanvasContext)
  const src = String(node.attrs.src ?? "")
  const imageSrc = src.startsWith("asset://")
    ? assets[src.slice(8)]
    : notebookMediaUrl(src)
  return (
    <NodeViewWrapper
      className="group/canvas-figure relative my-4"
      contentEditable={false}
    >
      {editor.isEditable && selected && (
        <div
          className="absolute -top-7 left-0 z-10 flex items-center gap-2 rounded border border-coval-border bg-surface-default px-1 text-coval-text"
          data-drag-handle
        >
          <Button icon variant="ghost" tooltip="Drag to move">
            <GripVertical />
          </Button>
          {node.type.name === "image" && (
            <input
              type="range"
              className="accent-coval-text"
              aria-label="Image width"
              min={10}
              max={100}
              value={Number(node.attrs.width ?? 100)}
              onChange={(event) =>
                updateAttributes({ width: Number(event.target.value) })
              }
            />
          )}
        </div>
      )}
      {node.type.name === "chart" ? (
        <NotebookPlot spec={node.attrs.spec as NotebookPlotSpec} />
      ) : node.type.name === "evidence" ? (
        renderEvidence(Number(node.attrs.index))
      ) : imageSrc ? (
        <NextImage
          src={imageSrc}
          alt={String(node.attrs.alt ?? "Illustration")}
          width={1536}
          height={1024}
          unoptimized
          referrerPolicy="no-referrer"
          style={{
            width: `${Number(node.attrs.width ?? 100)}%`,
            height: "auto",
          }}
        />
      ) : (
        <p role="status">
          Illustration unavailable. Replace it with a supported public HTTPS
          image if the original cannot load.
        </p>
      )}
    </NodeViewWrapper>
  )
}
const container = (name: string, tag: string, content: string) =>
  Node.create({
    name,
    group:
      name === "column" || (name.startsWith("details") && name !== "details")
        ? undefined
        : "block",
    content,
    defining: true,
    addAttributes: () =>
      name === "details"
        ? {
            open: {
              default: false,
              parseHTML: (element) => element.hasAttribute("open"),
              renderHTML: (attrs) => (attrs.open ? { open: "" } : {}),
            },
          }
        : {},
    parseHTML: () => [{ tag: `${tag}[data-notebook-node="${name}"]` }],
    renderHTML: ({ HTMLAttributes }) => [
      tag,
      mergeAttributes(HTMLAttributes, { "data-notebook-node": name }),
      0,
    ],
  })
export const NotebookColumns = container("columns", "div", "column{2,3}")
export const NotebookColumn = container("column", "div", "block+")
export const NotebookDetails = container(
  "details",
  "details",
  "detailsSummary detailsContent",
).extend({
  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement("details")
      dom.dataset.notebookNode = "details"
      dom.open = !!node.attrs.open
      let defaultOpen = dom.open
      const summaryClick = (event: Event) =>
        event.target instanceof Element &&
        event.target.closest("summary")?.parentElement === dom
      dom.addEventListener("click", (event) => {
        if (!summaryClick(event)) {
          return
        }
        event.preventDefault()
        if (event.detail < 2) {
          dom.open = !dom.open
        }
      })
      return {
        dom,
        contentDOM: dom,
        stopEvent: (event) => event.type === "click" && summaryClick(event),
        ignoreMutation: (mutation) =>
          mutation.type === "attributes" &&
          mutation.target === dom &&
          mutation.attributeName === "open",
        update: (next) => {
          if (next.type.name !== "details") {
            return false
          }
          if (!!next.attrs.open !== defaultOpen) {
            defaultOpen = !!next.attrs.open
            dom.open = defaultOpen
          }
          return true
        },
      }
    }
  },
})
export const NotebookDetailsSummary = container(
  "detailsSummary",
  "summary",
  "inline*",
)
export const NotebookDetailsContent = container(
  "detailsContent",
  "div",
  "block+",
)
export const NotebookChart = Node.create({
  name: "chart",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes: () => ({ spec: { default: null } }),
  parseHTML: () => [{ tag: 'div[data-notebook-node="chart"]' }],
  renderHTML: () => ["div", { "data-notebook-node": "chart" }],
  addNodeView: () => ReactNodeViewRenderer(FigureNode),
})
export const NotebookEvidence = Node.create({
  name: "evidence",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes: () => ({ index: { default: 0 } }),
  parseHTML: () => [{ tag: 'div[data-notebook-node="evidence"]' }],
  renderHTML: () => ["div", { "data-notebook-node": "evidence" }],
  addNodeView: () => ReactNodeViewRenderer(FigureNode),
})
export const NotebookImage = Node.create({
  name: "image",
  group: "block",
  atom: true,
  draggable: true,
  addAttributes: () => ({
    src: { default: null },
    alt: { default: null },
    title: { default: null },
    width: { default: 100 },
  }),
  parseHTML: () => [
    {
      tag: "img[src]",
      getAttrs: (element) =>
        element.getAttribute("src")?.startsWith("asset://") ||
        notebookMediaUrl(element.getAttribute("src"))
          ? null
          : false,
    },
  ],
  renderHTML: ({ HTMLAttributes }) => ["img", HTMLAttributes],
  addNodeView: () => ReactNodeViewRenderer(FigureNode),
})
